import { itx } from "../../../src/translationItx/itx";
import { RotateCw } from "lucide-react";
import { availableFurnitureEloDefinitions } from "../model/C1_furnitureEloOptions";
import { C1_ChannelTypeSelect } from "./C1_ChannelTypeSelect";
import { C1_LightProductSelect } from "../components/C1_LightProductSelect";
import type { C1_LightProductId } from "../data/C1_ProductDefinitions";
import { ChannelType } from "../../../src/models/ChannelType";
import type { C1_FurniturePropertyDefinition, FurnitureProps } from "../types/C1_FurnitureTypes";
import type { C1_State } from "../types/C1_ModelTypes";
import C1_EloEquipmentEditor from "./C1_EloEquipmentEdior";
import { furnitureById } from "../furniture/C1_furnitureRegistry";
import { isFurnitureDockedToWall } from "../furniture/C1_furnitureDocking";
import { getEloItemProfileForInstance } from "../data/C1_eloItemsRegistry";
import { useEditorLabelWidth } from "./C1_useEditorLabelWidth";
import type { C1_Furniture } from "../model/C1_Furniture";
import { isLightGroupDefinitionId } from "../furniture/C1_lightGroup";
import { C1_Room } from "../model/C1_Room";

type FurnitureEditorProps = {
  model: C1_State;
  furniture: C1_Furniture;
  showEloEquipment?: boolean;
  hiddenPropertyKeys?: readonly string[];
  showTitle?: boolean;
};

const CORE_PROP_KEYS = new Set([
  "width",
  "height",
  "rotation",
  "PX",
  "strokeWidth",
  "strokeColor",
  "fillColor",
  "showDetails",
]);

const ELECTRICAL_ONLY_FURNITURE_IDS = new Set([
  "electricalButton",
  "electricalLight",
  "electricalOutlet",
  "fixedConsumer",
  "fixedConsumer400",
  "outdoorLight",
  "outdoorOutletGroup",
  "outdoorButton",
]);

const baseProperties: C1_FurniturePropertyDefinition[] = [
  {
    editLevel: 1,
    key: "width",
    get label() { return itx("c1.ui.Breite", "Breite"); },
    get description() { return itx("c1.ui.Darstellungsbreite_im_Grundriss", "Darstellungsbreite im Grundriss."); },
    type: "number",
    min: 0.2,
    max: 10,
    step: 0.1,
    unit: "m",
  },
  {
    editLevel: 1,
    key: "height",
    get label() { return itx("c1.ui.Tiefe", "Tiefe"); },
    get description() { return itx("c1.ui.Darstellungshohe_im_Grundriss", "Darstellungshöhe im Grundriss."); },
    type: "number",
    min: 0.2,
    max: 10,
    step: 0.1,
    unit: "m",
  },
  {
    editLevel: 1,
    key: "rotation",
    get label() { return itx("c1.ui.Drehwinkel", "Drehwinkel"); },
    get description() { return itx("c1.ui.Drehung_des_Mobelstucks_im_Uhrzeigersinn", "Drehung des Möbelstücks im Uhrzeigersinn."); },
    type: "rotation",
    defaultValue: 0,
    min: 0,
    max: 360,
    step: 1,
    unit: "°",
  },
  {
    editLevel: 2,
    key: "strokeWidth",
    get label() { return itx("SettingsScreen.lineWidh", "Linienstärke"); },
    type: "number",
    min: 0.5,
    max: 10,
    step: 0.5,
    unit: "px",
  },
  {
    editLevel: 2,
    key: "strokeColor",
    get label() { return itx("c1.ui.Linienfarbe", "Linienfarbe"); },
    type: "color",
    defaultValue: "#000000",
  },
  {
    editLevel: 2,
    key: "fillColor",
    get label() { return itx("c1.ui.Fullfarbe", "Füllfarbe"); },
    type: "color",
    defaultValue: "#ffffff",
  },
  {
    editLevel: 2,
    key: "showDetails",
    get label() { return itx("c1.ui.Details_anzeigen", "Details anzeigen"); },
    type: "boolean",
    defaultValue: true,
  },
];

export function C1_FurnitureEditor({
  model,
  furniture,
  showEloEquipment = true,
  hiddenPropertyKeys = [],
  showTitle = true,
}: FurnitureEditorProps) {
  const editorRef = useEditorLabelWidth();
  const room = furniture.room;

  if (!room || !furniture) {
    return null;
  }
  const selectedFurniture = furniture;
  const definition = furnitureById[furniture.definitionId];

  if (!definition) {
    return null;
  }

  // Rotation is implied by the wall side only for furniture that actually docks/auto-rotates to a
  // wall; freestanding items (e.g. a staircase) keep manual rotation even when placed near a wall.
  const isDockedToWall =
    (definition.wallDockable === true || definition.autoRotate === true) &&
    furniture.parent instanceof C1_Room &&
    isFurnitureDockedToWall(furniture.relativePosition, room.innerRect(), room.placementGeometry());

  const values: FurnitureProps = {
    ...furniture.baseProps,
  };

  function updateValue(
    currentValues: FurnitureProps,
    definition: C1_FurniturePropertyDefinition,
    rawValue: string | boolean,
  ) {
    let value: string | number | boolean = rawValue;

    if (definition.type === "number") {
      value = Number(rawValue);
    }

    const apply = () => updateFurniture({ ...currentValues, [definition.key]: value });
    if (definition.type === "boolean") {
      globalThis.state.undoManager.runAsSingleStep(definition.label, apply);
    } else {
      apply();
    }
  }

  function updateFurniture(nextValues: FurnitureProps) {
    selectedFurniture.setBaseProps(nextValues);
  }

  const extProperties = definition.properties;

  const currentEditLevel = 1;
  const language = model.language;
  const electricalOnly = ELECTRICAL_ONLY_FURNITURE_IDS.has(furniture.definitionId);
  const hiddenProperties = new Set([...hiddenPropertyKeys, ...(electricalOnly ? ["width", "height", "rotation"] : [])]);
  return (
    <div
      ref={editorRef}
      className="C1_editor C1_furnitureEditor dockForm"
      onBlur={() => globalThis.state.undoManager.userInteractionEnded()}
    >
      {showTitle && <h1 className="dockTitle">{definition.description[language]}</h1>}
      <div className="content dockFormBody">
        <div className="dockFields">
          {[...baseProperties, ...extProperties].map((item) => {
            if (hiddenProperties.has(item.key)) {
              return null;
            }

            if (item.type === "rotation" && isDockedToWall) {
              return null;
            }

            const capabilityContext = {
              values,
              wallDocked: selectedFurniture.wallDocked !== undefined,
            };
            const definitionVisibility =
              item.key === "width" ? definition.grabsW : item.key === "height" ? definition.grabsH : undefined;
            if (definitionVisibility === false) return null;
            if (typeof definitionVisibility === "function" && !definitionVisibility(capabilityContext)) return null;
            if (item.visible && !item.visible(capabilityContext)) return null;

            if (item.editLevel && item.editLevel > currentEditLevel) {
              return null;
            }

            const value = values[item.key] ?? item.defaultValue;
            const dimensionalValue = item.key === "width" || item.key === "height" || ["m", "cm", "mm"].includes(item.unit ?? "");

            return (
              <div key={item.key} className="field dockField">
                <div className="label">
                  <span title={item.description}>{item.label}</span>
                </div>

                {item.type === "boolean" && (
                  <input
                    type="checkbox"
                    checked={Boolean(value)}
                    onChange={(event) => updateValue(values, item, event.target.checked)}
                  />
                )}

                {item.enum && (
                  <select
                    className="C1_editorSelect dockControl"
                    value={String(value)}
                    onChange={(event) => updateValue(values, item, event.target.value)}
                  >
                    {Object.entries(item.enum).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                )}

                {item.type === "number" && !item.enum && (
                  <input className="dockControl"
                    type="number"
                    value={dimensionalValue ? Math.round((Number(value) + Number.EPSILON) * 100) / 100 : Number(value)}
                    min={item.min}
                    max={item.max}
                    step={item.step}
                    onChange={(event) => updateValue(values, item, event.target.value)}
                  />
                )}

                {item.type === "rotation" && (
                  <>
                    <input
                      className="dockControl"
                      type="range"
                      value={Number(value)}
                      min={0}
                      max={360}
                      step={1}
                      onChange={(event) => updateValue(values, item, event.target.value)}
                    />
                    <button
                      type="button"
                      className="dockIconButton"
                      title={itx("c1.ui.Um_90_Grad_drehen", "Um 90° drehen")}
                      aria-label={itx("c1.ui.Um_90_Grad_drehen", "Um 90° drehen")}
                      data-testid="c1-furniture-rotate-90"
                      onClick={() => {
                        // Snaps to the nearest cardinal angle first so repeated clicks always land exactly
                        // on 0/90/180/270, switching cleanly between a horizontal and vertical orientation.
                        const next = (Math.round(Number(value) / 90) * 90 + 90) % 360;
                        globalThis.state.undoManager.runAsSingleStep(item.label, () => updateFurniture({ ...values, rotation: next }));
                      }}
                    >
                      <RotateCw size={16} aria-hidden="true" />
                    </button>
                  </>
                )}

                {item.type === "color" && (
                  <input
                    type="color"
                    value={String(value)}
                    onChange={(event) => updateValue(values, item, event.target.value)}
                  />
                )}

                {item.key === "channelType" && <C1_ChannelTypeSelect
                  value={value as ChannelType} isDali={values.isDali ?? false}
                  onChannelTypeChange={(next) => selectedFurniture.setLightChannelType(next)}
                  onDaliChange={(next) => selectedFurniture.setLightDali(next)}
                />}
                {item.key === "lightProductId" && (
                  <C1_LightProductSelect
                    value={typeof value === "string" ? value as C1_LightProductId : undefined}
                    options={(typeof item.options === "function" ? item.options(capabilityContext) : item.options ?? [])
                      .map((option) => String(option.value) as C1_LightProductId)}
                    onChange={(lightProductId) => {
                      updateFurniture({ ...values, lightProductId });
                      globalThis.state.undoManager.userInteractionEnded();
                    }}
                  />
                )}
                {item.type === "select" && item.key !== "channelType" && item.key !== "lightProductId" && (
                  <select
                    className="C1_editorSelect dockControl"
                    value={String(value)}
                    onChange={(event) => updateValue(values, item, event.target.value)}
                  >
                    {(typeof item.options === "function" ? item.options(capabilityContext) : item.options)?.map(
                      (option) => (
                        <option key={String(option.value)} value={option.value}>
                          {option.label}
                        </option>
                      ),
                    )}
                  </select>
                )}
              </div>
            );
          })}
          {definition.allowManualReflect && (
            <div className="field dockField">
              <div className="label">{itx("c1.ui.Spiegelung", "Spiegelung")}</div>
              <input
                type="checkbox"
                checked={furniture.reflect === true}
                onChange={(event) => {
                  furniture.setReflect(event.target.checked);
                  globalThis.state.undoManager.userInteractionEnded();
                }}
              />
            </div>
          )}
          {(showEloEquipment || electricalOnly) && !isLightGroupDefinitionId(furniture.definitionId) && (
            <C1_EloEquipmentEditor
              state={model}
              selectedItemId={model.selection?.id}
              rootDefinitionId={furniture.definitionId}
              eloTree={furniture.eloTree}
              eloDefinition={availableFurnitureEloDefinitions(getEloItemProfileForInstance(
                furniture.definitionId,
                furniture.eloTree,
              ), furniture.baseProps, furniture.wallDocked !== undefined)}
              onChange={(eloTree) => furniture.setEloTree(eloTree)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
